// ============================================================================
// AFB-0075 / W-09 — the pure half of "a template that carries its own document".
//
// Separate from envelope-lib.ts because that module imports pdfjs-dist at load
// time, which needs DOMMatrix and therefore cannot be imported by a node-
// environment test at all. Every other tested piece of this module (merge-vars,
// anchor-placement, field-detect, envelope-status) is pure for the same reason.
// The rules worth pinning are in here; the network calls stay there.
// ============================================================================

/** The document metadata carried on esign_templates. Bytes live elsewhere. */
export interface TemplateDocumentMeta {
  name: string;
  document_filename: string | null;
  document_bytes: number | null;
  document_hash: string | null;
}

/**
 * 12 MB of source PDF (~16 MB base64), mirroring the CHECK on
 * esign_template_documents so the browser refuses with a sentence before the
 * database refuses with a constraint name.
 */
export const MAX_TEMPLATE_DOCUMENT_BYTES = 12 * 1024 * 1024;

/**
 * True when this template can start an envelope without re-uploading a PDF.
 *
 * Keyed on BYTES, never on the filename or the hash. Metadata without bytes
 * should be impossible — esign_save_template_document writes both sides in one
 * transaction — so if it ever occurs it must read as "not ready", not as ready.
 */
export function templateHasDocument(t: Pick<TemplateDocumentMeta, 'document_bytes'>): boolean {
  return (t.document_bytes ?? 0) > 0;
}

/** Human size for a template's document. "—" when there isn't one. */
export function formatDocumentSize(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * Why this template cannot start an envelope on its own, or null if it can.
 *
 * Names the template and gives BOTH ways out. The feature exists because a
 * missing document made Antonela re-upload every time; a template that quietly
 * produced a blank envelope would be the same defect with an extra step, and a
 * refusal with no remedy would be the stage-gate dead end all over again.
 */
export function templateDocumentBlockedReason(
  t: Pick<TemplateDocumentMeta, 'name' | 'document_bytes'>,
): string | null {
  if (templateHasDocument(t)) return null;
  return (
    `"${t.name}" has no saved document, so it can't start an envelope on its own. ` +
    `Either attach a PDF here, or save one to the template first — after that this ` +
    `needs nothing but a title.`
  );
}

/** Why this file cannot be saved as a template document, or null if it can. */
export function templateDocumentRejectReason(
  file: Pick<File, 'size'>,
): string | null {
  if (file.size <= 0) return 'That file is empty.';
  if (file.size > MAX_TEMPLATE_DOCUMENT_BYTES) {
    return (
      `That PDF is ${formatDocumentSize(file.size)}. A template document has to be ` +
      `under ${formatDocumentSize(MAX_TEMPLATE_DOCUMENT_BYTES)} — above that it isn't ` +
      `boilerplate any more. Attach it to the envelope directly instead.`
    );
  }
  return null;
}
